/**
 * User Context - Provides centralized user data and installed apps management
 */

import { createContext, type ComponentChildren } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import { db, onAuthChange, signOut as firebaseSignOut } from '../globals/firebase';
import { doc, getDoc, setDoc, updateDoc, collection, onSnapshot, Timestamp } from 'firebase/firestore';
import { User } from 'firebase/auth';
import type { InstalledAppData } from '../globals/firebase';

export interface AuthState {
  user: User & { githubUsername: string, photoURL: string } | null;
  loading: boolean;
  initialized: boolean;
}

interface UserContextType {
  // User data
  user: AuthState;
  isAuthenticated: boolean;

  // User's installed apps
  installedApps: string[];

  // User's entitlements (purchased apps)
  entitlements: string[];

  // Methods
  installApp: (appId: string, version: string, name: string, iconUrl: string) => Promise<void>;
  uninstallApp: (appId: string) => Promise<void>;
  isAppInstalled: (appId: string) => boolean;
  isAppEntitled: (appId: string) => boolean;
  getInstalledVersion: (appId: string) => Promise<string | null>;
  signOut: () => Promise<void>;
}

interface UserData {
  reloadUserInfo: {
    screenName: string;
  };
}

const UserContext = createContext<UserContextType | null>(null);

export function UserProvider({ children }: { children: ComponentChildren }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    initialized: false,
  });
  const [installedApps, setInstalledApps] = useState<string[]>([]);
  const [entitlements, setEntitlements] = useState<string[]>([]);

  const isAuthenticated = authState.user !== null;

  // Set up auth state listener
  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthChange((user) => {
      const githubUsername = (user as User & UserData)?.reloadUserInfo?.screenName || '';
      const photoURL = (user as User & UserData)?.photoURL || '';
      setAuthState({
        user: user ? {
          ...user,
          githubUsername,
          photoURL,
        } : null,
        loading: false,
        initialized: true,
      });
    });

    return () => unsubscribe();
  }, []);

  // Set up real-time listeners for user data when user changes
  useEffect(() => {
    if (!authState.user || !authState.initialized) {
      // Clear data when user logs out
      setInstalledApps([]);
      setEntitlements([]);
      return;
    }

    const userId = authState.user.uid;

    // Set up listener for installed apps
    const installedAppsRef = collection(db, 'users', userId, 'installedApps');
    const unsubscribeInstalledApps = onSnapshot(
      installedAppsRef,
      (snapshot) => {
        const installedAppIds = snapshot.docs.map(doc => doc.data().appId);
        setInstalledApps(installedAppIds);
      },
      (error) => {
        console.error('Error listening to installed apps:', error);
      }
    );

    // Set up listener for entitlements
    const entitlementsRef = collection(db, 'users', userId, 'entitlements');
    const unsubscribeEntitlements = onSnapshot(
      entitlementsRef,
      (snapshot) => {
        const entitlementIds = snapshot.docs.map(doc => doc.data().appId);
        setEntitlements(entitlementIds);
      },
      (error) => {
        console.error('Error listening to entitlements:', error);
      }
    );

    // Cleanup listeners on unmount or user change
    return () => {
      unsubscribeInstalledApps();
      unsubscribeEntitlements();
    };
  }, [authState.user, authState.initialized]);


  const installApp = async (appId: string, version: string, name: string, iconUrl: string) => {
    if (!authState.user) {
      throw new Error('User must be authenticated to install apps');
    }

    const userId = authState.user.uid;

    try {
      // Add to installed apps collection
      const installedAppRef = doc(db, 'users', userId, 'installedApps', appId);
      const installedAppData: Omit<InstalledAppData, 'id'> = {
        appId,
        name,
        iconUrl,
        version,
        installedAt: Timestamp.now(),
        lastLaunchedAt: null,
      };

      await setDoc(installedAppRef, installedAppData);

      // No need to update local state - the onSnapshot listener will handle it
    } catch (err) {
      console.error('Error installing app:', err);
      throw err;
    }
  };

  const uninstallApp = async (appId: string) => {
    if (!authState.user) {
      throw new Error('User must be authenticated to uninstall apps');
    }

    const userId = authState.user.uid;

    try {
      // Update installed apps collection to mark as uninstalled
      const installedAppRef = doc(db, 'users', userId, 'installedApps', appId);
      await getDoc(installedAppRef).then(doc => {
        if (doc.exists()) {
          return updateDoc(installedAppRef, { status: 'uninstalled' });
        }
      });

      // No need to update local state - the onSnapshot listener will handle it
    } catch (err) {
      console.error('Error uninstalling app:', err);
      throw err;
    }
  };

  const isAppInstalled = (appId: string): boolean => {
    return installedApps.includes(appId);
  };

  const isAppEntitled = (appId: string): boolean => {
    return entitlements.includes(appId);
  };

  const getInstalledVersion = async (appId: string): Promise<string | null> => {
    if (!authState.user) {
      return null;
    }

    try {
      const userId = authState.user.uid;
      const installedAppRef = doc(db, 'users', userId, 'installedApps', appId);
      const installedAppDoc = await getDoc(installedAppRef);

      if (installedAppDoc.exists()) {
        const data = installedAppDoc.data() as InstalledAppData;
        return data.version || null;
      }

      return null;
    } catch (err) {
      console.error('Error getting installed version:', err);
      return null;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut();
      // Auth state listener will handle clearing user state
    } catch (err) {
      console.error('Error signing out:', err);
      throw err;
    }
  };

  const value: UserContextType = {
    user: authState,
    isAuthenticated,
    installedApps,
    entitlements,
    installApp,
    uninstallApp,
    isAppInstalled,
    isAppEntitled,
    getInstalledVersion,
    signOut,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}